// StoryRow.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { color } from '../style/variables.js';
import debounce from 'lodash/debounce';

import HydrationBoundary from '../component/HydrationBoundary.jsx';
import WithIntersectionObserver from './WithIntersectionObserver.jsx';
import { InfiniteScroller, ScrollItem } from './InfiniteScroller.jsx';
import ProfileCardItem from '../container/ProfileCardItem.js';
import MessageCard from '../container/MessageCard.js';
import Spinning from '../style/Spinning.js';
import media from '../style/media.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ProfileListType } from '../resource/profileConstants.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';

import LoadingImage from '../../img/loading.png';
import MoreIconSrc from '../../img/ic_arrow_right_white.svg';

const isServer = typeof window === 'undefined';

const MORE_BUTTON_HEIGHT = 26;
const MESSAGE_CARD_WIDTH = 128;
const MESSAGE_METADATA_CARD_WIDTH = 160;
const MESSAGE_CARD_HEIGHT = 172;
const MESSAGE_CARD_WIDTH_DESKTOP = 148;
const MESSAGE_METADATA_CARD_DESKTOP_WIDTH = 212;
const MESSAGE_CARD_HEIGHT_DESKTOP = 198;
const SHORTS_CARD_WIDTH = 160;
const SHORTS_METADATA_CARD_WIDTH = 112;
const SHORTS_CARD_HEIGHT = 240;
const SHORTS_CARD_WIDTH_DESKTOP = 212;
const SHORTS_METADATA_CARD_WIDTH_DESKTOP = 160;
const SHORTS_CARD_HEIGHT_DESKTOP = 320;
const MESSAGE_CARD_GAP = 8;

export class StoryRow extends React.PureComponent {
  state = { isFirstFrame: true };
  nextTicks = [];
  messagesElement = undefined;
  handleMessagesScroll = debounce(() => {
    const { badgeName, setScrollLeft } = this.props;
    if (this.messagesElement) {
      setScrollLeft({
        scrollLeft: this.messagesElement.scrollLeft,
        category: badgeName,
      });
    }
  }, 500); // TODO: remote config
  componentDidMount() {
    const { isFirstPageFetched } = this.props;
    if (window.__IS_HYDRATED__ || !isFirstPageFetched) {
      // don't need to hydrate since SSR data is empty.
      // mostly for local dev env.
      window.__IS_HYDRATED__ = 'StoryRow';
    }
    this.nextTicks.push(
      setTimeout(() => this.setState({ isFirstFrame: false }))
    );
  }

  componentDidUpdate(prevProps) {
    const { isAuthed: prevIsAuthed, feedUnixTimestamp: prevFeedUnixTimestamp } =
      prevProps;
    const {
      isAuthed,
      feedUnixTimestamp,
      shouldRefresh,
      fetchFirstPage,
      scrollLeft,
    } = this.props;
    const { isFirstFrame } = this.state;

    if (
      isAuthed !== prevIsAuthed ||
      feedUnixTimestamp !== prevFeedUnixTimestamp ||
      (shouldRefresh && shouldRefresh !== prevProps.shouldRefresh)
    ) {
      this.nextTicks.push(
        setTimeout(() => {
          fetchFirstPage({
            shouldSkipLocalCache: shouldRefresh,
          });
        })
      );
    }

    if (
      !isFirstFrame &&
      this.messagesElement &&
      scrollLeft > this.messagesElement.scrollLeft
    ) {
      this.messagesElement.scrollLeft = scrollLeft;
    }
  }

  componentWillUnmount() {
    clearTimeout(this.intersectionTimer);
    this.nextTicks.forEach(clearTimeout);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'StoryRow';
    }
  }

  handleIntersection = ({ isIntersecting }, unobserve) => {
    clearTimeout(this.intersectionTimer);
    if (isIntersecting) {
      this.intersectionTimer = setTimeout(() => {
        const { isFirstPageFetched, fetchFirstPage } = this.props;
        unobserve();
        if (!isFirstPageFetched) {
          fetchFirstPage();
        }
      }, 100); // TODO: remote config
    }
  };

  trackUserClick = ({ buttonId }) => {
    const { index, category } = this.props;
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        buttonId,
        extraProps: {
          'discover.index': index,
          'discover.category': category,
        },
      },
    });
  };

  renderPlaceholders() {
    const { deviceWidth, maxPlaceholdersAmount, isShorts, shouldShowMetadata } =
      this.props;

    const placeholderAmount = Math.min(
      Math.ceil(deviceWidth / (MESSAGE_CARD_WIDTH + MESSAGE_CARD_GAP)),
      maxPlaceholdersAmount
    );

    return Array(placeholderAmount)
      .fill(null)
      .map((element, index) => (
        <MessageCardWrapper
          key={index}
          isShorts={isShorts}
          shouldShowMetadata={shouldShowMetadata}
        >
          {shouldShowMetadata ? (
            <ProfileCardItem isThin={isShorts} />
          ) : (
            <MessageCard />
          )}
        </MessageCardWrapper>
      ));
  }

  renderHydrateStoryCard({
    messageId,
    index,
    category,
    listPath,
    isShorts,
    shouldShowMetadata,
    isPinned,
    isProfileCategory,
  }) {
    // Deliberately using `index` as the component key for Shorts
    // since the messageId is not unique in Shorts.
    return (
      <WithIntersectionObserver
        key={isShorts ? index : messageId}
        threshold={0.1}
      >
        {({ isIntersecting }) => {
          return (
            <HydrationBoundary
              shouldHydrate={isIntersecting}
              wrapper={
                <MessageCardWrapper
                  isShorts={isShorts}
                  shouldShowMetadata={shouldShowMetadata}
                />
              }
            >
              {shouldShowMetadata ? (
                <ProfileCardItem
                  messageId={messageId}
                  categoryId={category}
                  index={index}
                  isPinned={isPinned}
                  listType={
                    isShorts ? ProfileListType.SHORT : ProfileListType.STORY
                  }
                  isThin={isShorts}
                  isProfileCategory={isProfileCategory}
                  locationState={{ listPath }}
                />
              ) : (
                <MessageCard
                  id={messageId}
                  categoryIndex={index}
                  categoryId={category}
                  listPath={listPath}
                  shouldWrapLink
                  shouldShowUser
                  shouldTrackEvent
                  shouldShowTooltip
                  shouldUseLazyThumbnail={false}
                  isShorts={isShorts}
                />
              )}
            </HydrationBoundary>
          );
        }}
      </WithIntersectionObserver>
    );
  }

  renderStoryCard({
    messageId,
    index,
    category,
    listPath,
    isShorts,
    shouldShowMetadata,
    isPinned,
    isProfileCategory,
  }) {
    // Deliberately using `index` as the component key for Shorts
    // since the messageId is not unique in Shorts.
    return (
      <WithIntersectionObserver
        key={isShorts ? index : messageId}
        threshold={0.1}
      >
        {({ isIntersecting }) => {
          if (isIntersecting) {
            return (
              <MessageCardWrapper
                isShorts={isShorts}
                shouldShowMetadata={shouldShowMetadata}
              >
                {shouldShowMetadata ? (
                  <ProfileCardItem
                    messageId={messageId}
                    categoryId={category}
                    index={index}
                    isPinned={isPinned}
                    listType={
                      isShorts ? ProfileListType.SHORT : ProfileListType.STORY
                    }
                    isThin={isShorts}
                    isProfileCategory={isProfileCategory}
                    locationState={{ listPath }}
                  />
                ) : (
                  <MessageCard
                    id={messageId}
                    categoryIndex={index}
                    categoryId={category}
                    listPath={listPath}
                    shouldWrapLink
                    shouldShowUser
                    shouldTrackEvent
                    shouldShowTooltip
                    isShorts={isShorts}
                  />
                )}
              </MessageCardWrapper>
            );
          }

          return (
            <MessageCardWrapper
              isShorts={isShorts}
              shouldShowMetadata={shouldShowMetadata}
            />
          );
        }}
      </WithIntersectionObserver>
    );
  }

  render() {
    const {
      titleI18nId,
      moreLink,
      titleLink,
      nextPage,
      messageIds,
      pinnedMessageIds,
      isShorts,
      isNextPageFetching,
      isFirstPageFetched,
      shouldShowMoreLink,
      shouldShowMetadata,
      isProfileCategory,
      isTitleClickable,
      fetchNextPage,
      category,
      listPath,
      t,
    } = this.props;
    const { isFirstFrame } = this.state;

    const shouldHydrate = isServer || !window.__IS_HYDRATED__;
    const shouldRenderData = !isFirstFrame || shouldHydrate;

    const renderStoryCard = shouldHydrate
      ? this.renderHydrateStoryCard
      : this.renderStoryCard;

    const isFirstPageFetching = isNextPageFetching && messageIds.length === 0;

    if (isFirstPageFetched && !messageIds.length) {
      return null;
    }

    return (
      <StyledStoryRow isShorts={isShorts}>
        <Header>
          <HeaderLeft>
            <Title
              to={titleLink}
              as={isTitleClickable ? Link : undefined}
              replace={isProfileCategory}
            >
              {t(titleI18nId, {
                ns: TranslationNamespace.FEED,
              })}
            </Title>
          </HeaderLeft>
          <HeaderRight>
            {shouldShowMoreLink && (
              <CategoryMoreButton
                to={moreLink}
                onClick={() => {
                  this.trackUserClick({ buttonId: ButtonId.All.ButtonMore });
                }}
                title={`SWAG ${t(titleI18nId, {
                  ns: TranslationNamespace.FEED,
                })}`}
              >
                <MoreButtonText>
                  {t('more', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </MoreButtonText>
                <MoreButtonIcon
                  src={MoreIconSrc}
                  alt={t('more', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                />
              </CategoryMoreButton>
            )}
          </HeaderRight>
        </Header>
        <WithIntersectionObserver
          shouldKeepObserve={true}
          onChange={this.handleIntersection}
        >
          {() =>
            !shouldRenderData || isFirstPageFetching ? (
              <Messages>
                <MessageContainer>{this.renderPlaceholders()}</MessageContainer>
              </Messages>
            ) : (
              <Messages
                onScroll={this.handleMessagesScroll}
                ref={el => (this.messagesElement = el)}
              >
                <InfiniteScroller
                  axis="x"
                  useWindow={false}
                  pageStart={1}
                  hasMore={nextPage != null}
                  loadMore={fetchNextPage}
                  isFetching={isNextPageFetching}
                  threshold={100}
                >
                  <MessageContainer>
                    <ScrollItem
                      loader={
                        <MessageCardWrapper
                          isShorts={isShorts}
                          shouldShowMetadata={shouldShowMetadata}
                        >
                          <Loader>
                            <Loading src={LoadingImage} title="loading" />
                          </Loader>
                        </MessageCardWrapper>
                      }
                    >
                      {messageIds.length
                        ? messageIds.map((messageId, index) =>
                            renderStoryCard({
                              messageId,
                              index,
                              category,
                              listPath,
                              isShorts,
                              shouldShowMetadata,
                              isPinned: pinnedMessageIds.includes(messageId),
                              isProfileCategory,
                            })
                          )
                        : this.renderPlaceholders()}
                    </ScrollItem>
                  </MessageContainer>
                </InfiniteScroller>
              </Messages>
            )
          }
        </WithIntersectionObserver>
      </StyledStoryRow>
    );
  }
}

StoryRow.propTypes = {
  t: PropTypes.func.isRequired,
  badgeName: PropTypes.string.isRequired,
  titleI18nId: PropTypes.string.isRequired,
  moreLink: PropTypes.string.isRequired,
  titleLink: PropTypes.string.isRequired,
  nextPage: PropTypes.number,
  feedUnixTimestamp: PropTypes.number,
  messageIds: PropTypes.array.isRequired,
  pinnedMessageIds: PropTypes.array,
  isShorts: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  isAuthed: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  shouldShowMoreLink: PropTypes.bool,
  shouldShowMetadata: PropTypes.bool,
  isProfileCategory: PropTypes.bool,
  isTitleClickable: PropTypes.bool,
  category: PropTypes.string,
  fetchFirstPage: PropTypes.func,
  fetchNextPage: PropTypes.func,
  deviceWidth: PropTypes.number,
  index: PropTypes.number,
  maxPlaceholdersAmount: PropTypes.number,
  setScrollLeft: PropTypes.func,
  scrollLeft: PropTypes.number,
  listPath: PropTypes.arrayOf(PropTypes.string),
};

StoryRow.defaultProps = {
  category: '',
  titleI18nId: '',
  moreLink: '',
  titleLink: '',
  messageIds: [],
  pinnedMessageIds: [],
  nextPage: null,
  feedUnixTimestamp: null,
  isAuthed: false,
  shouldRefresh: false,
  shouldShowMoreLink: true,
  shouldShowMetadata: false,
  isProfileCategory: false,
  isTitleClickable: false,
  isShorts: false,
  isFirstPageFetched: false,
  isNextPageFetching: false,
  fetchFirstPage: () => null,
  fetchNextPage: () => null,
  deviceWidth: 375,
  index: null,
  maxPlaceholdersAmount: 10,
  setScrollLeft: () => null,
  scrollLeft: 0,
  listPath: [],
};

const StyledStoryRow = styled.article``;

const Header = styled.header`
  padding: 0px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
`;

const HeaderRight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 8px;
`;

const CategoryMoreButton = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${color.white};
  height: ${MORE_BUTTON_HEIGHT}px;
`;

const MoreButtonText = styled.span`
  margin-right: 4px;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 600;
  ${media.mobile`
    font-size: 12px;
  `};
`;

const MoreButtonIcon = styled.img`
  height: 16px;
  width: 16px;
`;

const Title = styled.h2`
  margin: 0;
  display: block;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
  ${media.mobile`
    font-size: 18px;
  `}
`;

const Messages = styled.div`
  padding: 12px 16px 0px;
  overflow-x: auto;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MessageContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const Loader = styled.div`
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  overflow: hidden;
`;

const Loading = styled.img`
  height: auto;
  width: 50%;
  animation: 1.4s infinite ${Spinning};
`;

const MessageCardWrapper = styled.div`
  width: ${({ isShorts, shouldShowMetadata }) =>
    isShorts
      ? shouldShowMetadata
        ? SHORTS_METADATA_CARD_WIDTH_DESKTOP
        : SHORTS_CARD_WIDTH_DESKTOP
      : shouldShowMetadata
        ? MESSAGE_METADATA_CARD_DESKTOP_WIDTH
        : MESSAGE_CARD_WIDTH_DESKTOP}px;
  height: ${({ isShorts, shouldShowMetadata }) =>
    shouldShowMetadata
      ? 'auto'
      : isShorts
        ? `${SHORTS_CARD_HEIGHT_DESKTOP}px`
        : `${MESSAGE_CARD_HEIGHT_DESKTOP}px`};
  margin-right: 8px;
  :last-child {
    margin-right: 0;
  }
  ${media.mobile`
    width: ${({ isShorts, shouldShowMetadata }) =>
      isShorts
        ? shouldShowMetadata
          ? SHORTS_METADATA_CARD_WIDTH
          : SHORTS_CARD_WIDTH
        : shouldShowMetadata
          ? MESSAGE_METADATA_CARD_WIDTH
          : MESSAGE_CARD_WIDTH}px;
    height: ${({ isShorts, shouldShowMetadata }) =>
      shouldShowMetadata
        ? 'auto'
        : isShorts
          ? `${SHORTS_CARD_HEIGHT}px`
          : `${MESSAGE_CARD_HEIGHT}px`};
  `};
`;

export default withTranslation()(StoryRow);
